import {CommonModule} from '@angular/common';
import {NgModule, Optional} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NavigationEnd, Router} from '@angular/router';
import {HttpClientModule} from '@angular/common/http';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {QRCodeModule} from 'angularx-qrcode';
import {ExpressionCompilerModule} from '@ngmedax/expression-compiler';
import {QuestionnaireSelectModule} from '@ngmedax/questionnaire-select';
import {Select2Module} from '@ngmedax/select2';
import {QuestionnaireOriginModule} from '@ngmedax/questionnaire-origin';

import {LayoutService} from '@ngmedax/layout';
import {ConfigService} from '@ngmedax/config';
import {ErrorService} from '@ngmedax/error';
import {MasterDataModule} from '@ngmedax/master-data';

import {configKeys} from './workflow.config-keys';
import {routing} from './workflow.routing';
import {NAVIGATION} from './workflow.navigation';
import {TruncatePipe} from './pipes/truncate.pipe';
import {StriptagsPipe} from './pipes/striptags.pipe';
import {HtmlDecodePipe} from './pipes/htmldecode.pipe';
import {DatexPipe} from './pipes/datex.pipe';
import {WorkflowService} from './services/workflow.service';
import {QuestionnairePathHashService} from './services/questionnaire/path-hash.service';
import {WorkflowInstanceGridComponent} from './components/workflow-instance-grid.component';
import {WorkflowGridComponent} from './components/workflow-grid.component';
import {WorkflowCrudComponent} from './components/workflow-crud.component';
import {WorkflowConditionsComponent} from './components/conditions/workflow-conditions.component';
import {WorkflowConditionsEditorComponent} from './components/conditions/editor/workflow-conditions-editor.component';
import {WorkflowConditionsGroupComponent} from './components/conditions/editor/workflow-conditions-group.component';
import {WorkflowConditionsRowComponent} from './components/conditions/editor/workflow-conditions-row.component';
import {WorkflowStageConditionsComponent} from './components/conditions/workflow-stage-conditions.component';
import {QrCodeModalComponent} from './components/modals/qrcode-modal.component';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {TRANSLATION_GRID_SCOPE} from '../../constants';
import {KEYS} from '../../translation-keys';
import {filter} from 'rxjs/operators';
import {IMaskModule} from 'angular-imask';



// hack to inject decorator declarations. must occur before class declaration!
export interface WorkflowModule extends Translatable {}

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    QuestionnaireSelectModule,
    Select2Module,
    QuestionnaireOriginModule,
    QRCodeModule,
    NgbModule,
    ExpressionCompilerModule,
    MasterDataModule,
    IMaskModule,
    routing
  ],
  declarations: [
    WorkflowInstanceGridComponent,
    WorkflowGridComponent,
    WorkflowCrudComponent,
    WorkflowConditionsComponent,
    WorkflowConditionsEditorComponent,
    WorkflowConditionsGroupComponent,
    WorkflowConditionsRowComponent,
    WorkflowStageConditionsComponent,
    QrCodeModalComponent,
    HtmlDecodePipe,
    StriptagsPipe,
    TruncatePipe,
    DatexPipe
  ],
  exports: [],
  providers: [
    WorkflowService,
    QuestionnairePathHashService
  ]
})
@Translatable({scope: TRANSLATION_GRID_SCOPE, keys: KEYS})
export class WorkflowModule {
  public constructor(
    private router: Router,
    private layoutService: LayoutService,
    private configService: ConfigService,
    private errorService: ErrorService,
    @Optional() private translationService: TranslationService,
  ) {
    layoutService.addMenuEntry(NAVIGATION);

    // subscribe to navigation end event
    const subscription = router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        const apiUri = this.configService.get(configKeys.WORKFLOW_API_URI_CONFIG_KEY);

        if (!apiUri) {
          // unsubscribe to prevent redirect loop
          subscription.unsubscribe();

          this.showErrorMessage();
        }
      });
  }

  /**
   * Adds a "workflow api url not found" message to the error service and redirects to the error page.
   */
  private showErrorMessage() {
    // uri for the error message
    const uri = 'workflow-api-uri-not-configured';

    // title that we will see on the error page
    const title = this._(KEYS.GRID.CONFIG_ERROR);

    // message that we will see on the error page
    const message = this._(KEYS.GRID.ERROR_MISSING_MANDATORY_CONFIG) + '<br/><br/>';

    // example config object that we will se on the error page
    const exampleObject = {
      'apis': {
        'workflow': {
          'uri': 'https://some.tld/api'
        }
      }
    };

    // add error message and redirect to error page
    // note: error service will only redirect when we are not already on this error page
    this.errorService
      .addErrorMessage(uri, title, message, exampleObject)
      .navigateToError(uri);
  }
}
